import { PAGINATION } from '@utazzo/shared';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { Density, DataGridState } from '@/components/data-grid/types';

import { rememberListLocation } from '@/hooks/use-list-return';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_DENSITY: Density = 'comfortable';

/// Server-side `PaginationQuerySchema` caps `pageSize` at
/// `PAGINATION.MAX_PAGE_SIZE` (200) and refuses 0/negative values.
/// We sanitise both at the URL-read boundary and the setter boundary so
/// a stale bookmark like `?pageSize=500` lands on a 200-row request
/// instead of a 400.
const clampPageSize = (n: number): number =>
  Math.min(
    Math.max(1, Number.isFinite(n) ? Math.trunc(n) : DEFAULT_PAGE_SIZE),
    PAGINATION.MAX_PAGE_SIZE,
  );
const clampPage = (n: number): number =>
  Math.max(1, Number.isFinite(n) ? Math.trunc(n) : DEFAULT_PAGE);

/** Stable empty default so the `hidden` parser keeps a constant reference when
 *  a page declares no default-hidden columns (avoids needless nuqs churn). */
const NO_HIDDEN: string[] = [];

/** Stable empty default so the filter parsers keep a constant reference when a
 *  page declares no default filter values. */
const NO_DEFAULT_FILTERS: Readonly<Record<string, string>> = {};

export interface UseDataGridStateOptions {
  defaultPageSize?: number;
  defaultSortBy?: string;
  defaultSortDir?: 'asc' | 'desc';
  /** Filter ids exposed by the page. Each is mapped to a URL key `f.${id}`. */
  filterIds?: ReadonlyArray<string>;
  /**
   * Column ids hidden on first load (no `hidden` in the URL). Seeds the
   * `hidden` state so a dense table can ship a decluttered default while the
   * user re-enables any column from the visibility menu (their choice is then
   * persisted in the URL). Pass a STABLE array - it's the nuqs default.
   */
  defaultHidden?: ReadonlyArray<string>;
  /**
   * Filter values applied on first load (no `f.*` in the URL) - e.g. the
   * itinerary dashboard opening on "Sent to customer". Seeds each filter's
   * nuqs default, so the value is active without polluting the URL and the
   * operator can still clear/change it (which then IS written to the URL).
   * Pass a STABLE object - it's the parser default.
   */
  defaultFilters?: Readonly<Record<string, string>>;
}

export interface UseDataGridStateResult {
  state: DataGridState;
  setSearch: (q: string) => void;
  setFilter: (id: string, value: string) => void;
  setSort: (sortBy: string | null, sortDir?: 'asc' | 'desc') => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setDensity: (density: Density) => void;
  setHidden: (hidden: string[]) => void;
}

/**
 * URL-synced state for a single DataGrid on a page. All keys are scoped flat
 * under `URLSearchParams` so this hook should be called at most once per
 * route. The fixed keys are: q, page, pageSize, sortBy, sortDir, density,
 * hidden. Feature-defined filter ids are added under `f.${id}`.
 */
export function useDataGridState(options: UseDataGridStateOptions = {}): UseDataGridStateResult {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    defaultSortBy,
    defaultSortDir = 'desc',
    filterIds = [],
    defaultHidden = NO_HIDDEN,
    defaultFilters = NO_DEFAULT_FILTERS,
  } = options;

  const [core, setCore] = useQueryStates({
    q: parseAsString.withDefault(''),
    page: parseAsInteger.withDefault(DEFAULT_PAGE),
    pageSize: parseAsInteger.withDefault(defaultPageSize),
    sortBy: parseAsString.withDefault(defaultSortBy ?? ''),
    sortDir: parseAsStringEnum<'asc' | 'desc'>(['asc', 'desc']).withDefault(defaultSortDir),
    density: parseAsStringEnum<Density>(['compact', 'comfortable']).withDefault(DEFAULT_DENSITY),
    hidden: parseAsArrayOf(parseAsString).withDefault(defaultHidden as string[]),
  });

  // Build the filter parser map dynamically from filterIds. Each filter is a
  // simple string with default ''. Keys are prefixed 'f.' in the URL so they
  // can't collide with the fixed keys above.
  const filterParsers = useMemo(() => {
    const map: Record<string, ReturnType<typeof parseAsString.withDefault>> = {};
    for (const id of filterIds) {
      map[`f.${id}`] = parseAsString.withDefault(defaultFilters[id] ?? '');
    }
    return map;
  }, [filterIds, defaultFilters]);

  const [filtersRaw, setFiltersRaw] = useQueryStates(filterParsers);

  // ─── "Back to the list" memory (see use-list-return.ts) ───
  //
  // Every list screen's page/pageSize/search/sort/filter state lives in the
  // query string, and every detail page's back button used to throw it away by
  // navigating to a hardcoded bare path. Recording it here - the one hook all
  // of them share - means each list is remembered without opting in.
  //
  // Reading `window.location.search` rather than React Router's `location`
  // snapshot is deliberate: nuqs writes the address bar with a raw
  // `history.replaceState` that React Router never sees, so its snapshot is
  // frozen at the last real navigation (same trap `useUrlParamWriter`
  // documents). And because those writes are THROTTLED, a read is only
  // trustworthy once the setter's promise has resolved.
  const { pathname } = useLocation();
  const record = useCallback(() => {
    rememberListLocation(pathname, window.location.search);
  }, [pathname]);

  // Arriving at the list (mount, or a real navigation to it) - covers deep
  // links, bookmarks, and the restore this whole mechanism performs. A bare
  // path records nothing, which is what clears a stale entry.
  useEffect(() => {
    record();
  }, [record]);

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const id of filterIds) {
      const v = filtersRaw[`f.${id}`];
      if (typeof v === 'string' && v !== '') {
        out[id] = v;
      }
    }
    return out;
  }, [filterIds, filtersRaw]);

  const state: DataGridState = useMemo(
    () => ({
      q: core.q,
      page: clampPage(core.page),
      pageSize: clampPageSize(core.pageSize),
      sortBy: core.sortBy === '' ? null : core.sortBy,
      sortDir: core.sortDir,
      density: core.density,
      hidden: core.hidden,
      filters,
    }),
    [core, filters],
  );

  return {
    state,
    setSearch: (q) => {
      void setCore({ q, page: DEFAULT_PAGE }).then(record);
    },
    setFilter: (id, value) => {
      // Country / City are independent URL keys, but a city belongs to exactly
      // one country - so switching Country has to drop a city picked under the
      // previous one, in the SAME write. Left behind, it contradicts the new
      // country and the list comes back empty with nothing on screen to
      // explain why. Grids without a City filter never hit this branch.
      const clearsCity = id === 'countryId' && Boolean(filtersRaw['f.cityId']);
      void Promise.all([
        setFiltersRaw({ [`f.${id}`]: value, ...(clearsCity ? { 'f.cityId': '' } : {}) }),
        setCore({ page: DEFAULT_PAGE }),
      ]).then(record);
    },
    setSort: (sortBy, sortDir) => {
      void setCore({
        sortBy: sortBy ?? '',
        sortDir: sortDir ?? (sortBy ? 'asc' : defaultSortDir),
        page: DEFAULT_PAGE,
      }).then(record);
    },
    setPage: (page) => {
      void setCore({ page: clampPage(page) }).then(record);
    },
    setPageSize: (pageSize) => {
      void setCore({ pageSize: clampPageSize(pageSize), page: DEFAULT_PAGE }).then(record);
    },
    setDensity: (density) => {
      void setCore({ density }).then(record);
    },
    setHidden: (hidden) => {
      void setCore({ hidden }).then(record);
    },
  };
}
